import {useLocale, useUniversalFetch} from '#imports';
import {getDefaultCitiesSuggestionsForLocale, defaultCitiesSuggestions} from '@malt/location-autocomplete/get-default-cities-suggestions';
import type {Location} from '@malt/nuxt-common-components-module/types';

type BuildOptions = {
    remoteLabel?: string | null;
    globalMatching: boolean;
    countryCode?: string | null;
    layers?: string[];
};

async function useResolveCountryLabelInternal(code: string): Promise<string> {
    try {
        const currentLocale = useLocale();
        const regionNames = new Intl.DisplayNames(currentLocale.languageCode.value, {type: 'region'});
        return regionNames.of(code) ?? code;
    } catch (e) {
        try {
            const country = await useUniversalFetch<{code: string; label: string}>(`/api/countries/${code}`);
            return country.label;
        } catch (e2) {
            return code;
        }
    }
}

async function useBuildDefaultLocationSuggestions(options: BuildOptions): Promise<Location[]> {
    const result: Location[] = [];

    if (options.remoteLabel && options.remoteLabel !== '') {
        result.push({formattedAddress: options.remoteLabel, remoteAllowed: true});
    }

    if (options.globalMatching) {
        result.push(...defaultCitiesSuggestions.globalMatching);
    } else {
        const {locale} = useLocale();
        result.push(...getDefaultCitiesSuggestionsForLocale(locale.value));
    }

    if (options.countryCode && options.layers?.includes('COUNTRY')) {
        const label = await useResolveCountryLabelInternal(options.countryCode);
        result.push({
            country: label,
            countryCode: options.countryCode,
            formattedAddress: label,
        });
    }

    return result;
}

export {useBuildDefaultLocationSuggestions as buildDefaultLocationSuggestions};
